import { StoryConfig } from "@/api/story-configs-types";
import { RoleName } from "@/api/users-types";
import { CreateStoryConfig } from "@/components/story-configs/CreateStoryConfig";
import { ApiErrorAlert } from "@/components/ui/ApiErrorAlert";
import { Link } from "@/components/ui/Link";
import { NoPermissions } from "@/components/ui/NoPermissions";
import { useHasRole } from "@/hooks/use-has-role";
import { useStoryConfigs } from "@/queries/story-configs/use-story-configs";
import { useStoryConfigsDatagridStore } from "@/stores/story-configs-datagrid-store";
import { storyTypeOptions, timestampColumns } from "@/utils/datagrid-utils";
import { getLatest } from "@/utils/get-latest";
import { Stack } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";

const columns: GridColDef<StoryConfig>[] = [
  {
    field: "id",
    headerName: "ID",
    width: 120,
    renderCell: (params) => (
      <Link to={`/story-configs/${params.row.id}`}>{params.row.id}</Link>
    ),
  },
  {
    field: "storyType",
    headerName: "Story type",
    width: 120,
    type: "singleSelect",
    valueOptions: storyTypeOptions,
  },
  {
    field: "revisions",
    headerName: "Revision",
    width: 120,
    type: "number",
    valueGetter: (_, row) => getLatest(row.revisions)?.revision || 0,
    align: "left",
    headerAlign: "left",
  },
  {
    field: "revisionNote",
    headerName: "Revision note",
    width: 300,
    valueGetter: (_, row) => getLatest(row.revisions)?.revisionNote || "",
  },
  ...timestampColumns<StoryConfig>(),
];

export function StoryConfigListPage() {
  const canCreate = useHasRole(RoleName.promptEngineer);
  const canView = useHasRole([
    RoleName.promptEngineer,
    RoleName.episodeManager,
  ]);

  const { data, isLoading, error } = useStoryConfigs();

  const { sortModel, filterModel, setSortModel, setFilterModel } =
    useStoryConfigsDatagridStore();

  if (!canView) {
    return <NoPermissions />;
  }

  return (
    <Stack gap={2} mb={2}>
      <ApiErrorAlert error={error} />
      <DataGrid
        rowHeight={46}
        columnHeaderHeight={46}
        columns={columns}
        filterModel={filterModel}
        loading={isLoading}
        pageSizeOptions={[25, 50, 100]}
        rows={data?.items}
        sortModel={sortModel}
        onFilterModelChange={setFilterModel}
        onSortModelChange={setSortModel}
      />
      {canCreate && (
        <Stack justifyContent={"flex-end"} direction={"row"} gap={1}>
          <CreateStoryConfig />
        </Stack>
      )}
    </Stack>
  );
}
